'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { useActionState, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { shallow } from 'zustand/shallow';
import { useStableCallback } from './useStableCallback.js';
/**
 * Creates a Form JSX component to handle optimistic updates and sync client form state to server.
 *
 * @param root0 Factory dependencies
 * @param root0.store Form store instance
 * @param root0.mutationApi Tanstack mutation API instanace
 * @returns Form JSX component
 */
export function createForm({
  store,
  mutationApi
}) {
  const {
    mutate,
    mutateDebounce
  } = mutationApi;
  return function Form({
    children,
    action,
    isOptimisticOnChange,
    onChange: userOnChange,
    onSubmit: userOnSubmit,
    onSuccess,
    ...htmlFormProps
  }) {
    const [, formAction] = useActionState(action, {
      status: 'IDLE'
    });
    const formRef = useRef(null);
    const isPendingChange = useRef(false);
    const formStore = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
    const prevStore = useRef(store.getSnapshot().fields);
    useEffect(() => {
      if (shallow(prevStore.current, formStore.fields)) {
        return;
      }
      if (!formRef.current) {
        return;
      }
      prevStore.current = formStore.fields;
      mutateDebounce(new FormData(formRef.current));
    }, [formStore.fields]);
    const stableUserOnSubmit = useStableCallback(userOnSubmit);
    const stableUserOnChange = useStableCallback(userOnChange);
    const handleSubmit = useCallback(event => {
      event.preventDefault();
      const form = event.currentTarget;
      const formData = new FormData(form);
      stableUserOnSubmit(event);
      if (isPendingChange.current) {
        return;
      }
      mutate(formData, {
        onSuccess
      });
    }, [stableUserOnSubmit, onSuccess]);
    const handleChange = useCallback(event => {
      stableUserOnChange(event);
      if (!isOptimisticOnChange) {
        return;
      }
      event.preventDefault();
      const form = event.currentTarget;
      const formData = new FormData(form);
      isPendingChange.current = true;
      const optimisticValues = mutateDebounce(formData, {
        onSettled: () => {
          isPendingChange.current = false;
        }
      });
      if (optimisticValues) {
        store.updateStore({
          fields: optimisticValues
        });
      }
    }, [isOptimisticOnChange, stableUserOnChange]);
    return _jsx("form", {
      ...htmlFormProps,
      onSubmit: handleSubmit,
      onChange: handleChange,
      action: formAction,
      ref: formRef,
      children: children
    });
  };
}